import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Paper,
} from "@material-ui/core";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BootstrapTable } from "react-bootstrap-table";

import _ from "lodash";
import "./Crud.css";
import "./GroupedReport.css";
import Loader from "./loader/Loader";

function groupData(data, process) {
  return data.reduce((acc, item, index) => {
    // Destructure the needed properties
    const { GroupBy, Agent, Group1, Group2, ...rest } = item;

    const object = {
      ...rest,
      Id: index,
    };

    if (!acc[GroupBy]) acc[GroupBy] = {};
    if (!acc[GroupBy][Group1]) acc[GroupBy][Group1] = {};
    if (!acc[GroupBy][Group1][Group2]) acc[GroupBy][Group1][Group2] = [];

    // Append the rest of the item to the nested array
    acc[GroupBy][Group1][Group2].push(
      process instanceof Function ? process(object) : object
    );

    return acc;
  }, {});
}

const order = (key) => {
  if (/^[!-/:-@[-`{-~]/.test(key)) return `0${key}`; // Matches special characters, prefix with '0' to sort first
  if (/^\d/.test(key)) return `1${key}`; // Matches numbers, prefix with '1' to sort after special characters
  return `2${key}`; // Default for others, prefix with '2' to sort last
};

const groupByMap = {
  0: "None",
  1: "Office",
  2: "Player",
};

function GameStatisticReport({
  interval,
  controlledData,
  className = "",
  initialData = {},
  title,
  getUrl,
  getParams,
  getProcess,
  getTotals,
  children,
}) {
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("userInfo")),
    []
  );
  const [groupBy, setGroupBy] = useState(() => getParams(userInfo)?.GroupBy);
  const [groupedData, setGroupedData] = useState(initialData);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const cancelTokenSource = useRef(null);

  useEffect(() => {
    if (controlledData) {
      setData(controlledData);
    }
  }, [controlledData]);

  useEffect(() => {
    const params =
      getParams instanceof Function ? getParams(userInfo) : undefined;
    setGroupBy(params.GroupBy);
  }, [getParams, userInfo]);

  /**
   * GET
   */
  const requestData = useCallback(() => {
    const params =
      getParams instanceof Function ? getParams(userInfo) : undefined;

    const currentCancelToken = cancelTokenSource.current;
    cancelTokenSource.current = axios.CancelToken.source();

    if (currentCancelToken) {
      currentCancelToken.cancel("Canceling previous request");
    }

    setLoading(true);
    const url = process.env.REACT_APP_API2_URL + getUrl(userInfo);
    axios
      .post(url, params, {
        cancelToken: cancelTokenSource.current.token,
      })
      .then((response) => response.data)
      .then((data) => {
        const groupedData = groupData(data, getProcess);
        setData(data);
        setGroupedData(groupedData);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error(err);
        }
      });

    return () => {
      currentCancelToken.cancel("Component unmounted");
    };
  }, [getUrl, userInfo, getProcess, getParams]);

  /**
   * Initial load
   */
  useEffect(() => {
    let intervalId = 0;

    if (getUrl) {
      if (interval) {
        intervalId = setInterval(() => {
          requestData();
        }, interval); // Will trigger every second
      }
    }

    return () => clearInterval(intervalId);
  }, [getUrl, requestData, interval]);

  const onSearch = useCallback(() => {
    requestData();
  }, [requestData]);

  /**
   * TABLE OPTIONS
   */
  const options = {
    page: 1, // which page you want to show as default
    sizePerPage: 20, // which size per page you want to locate as default
    pageStartIndex: 1, // where to start counting the pages
    paginationSize: 5, // the pagination bar size.
    prePage: "Prev", // Previous page button text
    nextPage: "Next", // Next page button text
    firstPage: "First", // First page button text
    lastPage: "Last", // Last page button text
    paginationShowsTotal: true, // Accept bool or function
    paginationPosition: "top", // default is bottom, top and both is all available
    withFirstAndLast: true, // Hide the going to First and Last page button
  };

  const groupedComponent = (sports) =>
    _.map(sports, (typeGroup, sport) => (
      <Accordion
        defaultExpanded
        key={sport}
        className="border border-gray-200 !rounded-xl"
      >
        <AccordionSummary>
          {/* AGENT  */}
          <div className="flex flex-col grouped-report-title">
            <span>Sport:</span>
            <h3>{sport}</h3>
          </div>
        </AccordionSummary>
        <AccordionDetails className="flex flex-col">
          {/* SPORT  */}
          <div className="flex flex-col gap-4 border border-gray-200 !rounded-xl p-4">
            {_.map(typeGroup, (game, type) => (
              <React.Fragment key={type}>
                {/* TYPE  */}
                <div className="flex flex-col grouped-report-title">
                  <span>Type:</span>
                  <h3>{type}</h3>
                </div>
                <div>
                  <BootstrapTable
                    className="text-xs"
                    data={game}
                    options={options}
                    version="4"
                    hover
                  >
                    {children}
                  </BootstrapTable>
                </div>
                {getTotals ? (
                  <div>
                    <hr className="border-gray-200" />
                    <BootstrapTable
                      className="text-xs font-bold"
                      data={[
                        getTotals instanceof Function
                          ? getTotals(game)
                          : getTotals,
                      ]}
                      version="4"
                      hover
                      headerContainerClass="hidden"
                    >
                      {children}
                    </BootstrapTable>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
          </div>
          {getTotals ? (
            <div className="px-6">
              <hr className="border-gray-200" />
              <BootstrapTable
                className="text-xs font-bold"
                data={[
                  getTotals instanceof Function
                    ? {
                        ...getTotals(_.flatten(_.values(typeGroup))),
                        Group3: `Sport: ${sport}`,
                      }
                    : getTotals,
                ]}
                version="4"
                hover
                headerContainerClass="hidden"
              >
                {children}
              </BootstrapTable>
            </div>
          ) : null}
        </AccordionDetails>
      </Accordion>
    ));

  if (loading) return <Loader />;
  return (
    <div id="main-div" className={`${className} !h-max flex flex-col gap-4`}>
      <div className="flex flex-row gap-4 mb-4">
        <Button
          type="button"
          onClick={onSearch}
          color="primary"
          variant="contained"
        >
          Search
        </Button>
      </div>
      <h2>{title}</h2>
      {!_.isEmpty(groupedData) ? (
        <Paper className="flex flex-col gap-4">
          {groupBy === 0
            ? groupedComponent(groupedData[""])
            : _.map(groupedData, (sportGroup, group) => (
                <Accordion
                  defaultExpanded
                  key={group}
                  className="border border-gray-200 !rounded-xl"
                >
                  <AccordionSummary>
                    {/* Sport  */}
                    <div className="flex flex-col grouped-report-title">
                      <span>{groupByMap[groupBy]}:</span>
                      <h3>{group}</h3>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="flex flex-col gap-4">
                    {groupedComponent(sportGroup)}
                    {getTotals ? (
                      <div className="px-10">
                        <hr className="border-gray-200" />
                        <BootstrapTable
                          className="text-xs font-bold"
                          data={[
                            getTotals instanceof Function
                              ? {
                                  ...getTotals(
                                    _.values(sportGroup)
                                      .map((value) => _.values(value))
                                      .flat(Infinity)
                                  ),
                                  Group3: `Group: ${group}`,
                                }
                              : getTotals,
                          ]}
                          version="4"
                          hover
                          headerContainerClass="hidden"
                        >
                          {children}
                        </BootstrapTable>
                      </div>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              ))}
          {getTotals ? (
            <div className="px-14">
              <hr className="border-gray-200" />

              <BootstrapTable
                className="text-xs font-bold"
                data={[
                  getTotals instanceof Function
                    ? {
                        ...getTotals(data),
                        Group4: `Totals`,
                      }
                    : getTotals,
                ]}
                version="4"
                hover
                headerContainerClass="hidden"
              >
                {children}
              </BootstrapTable>
            </div>
          ) : null}
        </Paper>
      ) : null}
    </div>
  );
}

export default GameStatisticReport;
